import { Plus, Trash2, CheckCircle2, Upload, AlertTriangle, Scale } from "lucide-react";

export const inputCls = "w-full px-3 py-2 bg-white border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-phoenix-navy/30";
export const labelCls = "block text-xs font-semibold text-phoenix-navy uppercase tracking-wider mb-1.5";

export const EMPTY_LICENSE = { state: "", license_type: "", license_number: "", expiration_date: "" };
export const EMPTY_DEGREE = { institution: "", field_of_study: "", degree_level: "Master's", graduation_date: "" };
export const DEGREE_LEVELS = ["Associate's", "Bachelor's", "Master's", "Doctorate (Ph.D./Psy.D.)", "M.D.", "D.O.", "N.P.", "P.A."];

export function Section({ title, children, when = true }) {
    if (!when) return null;
    return (
        <div className="bg-white border border-border/70 rounded-xl p-6">
            <h2 className="font-display font-semibold text-lg text-phoenix-navy mb-4">{title}</h2>
            {children}
        </div>
    );
}

export function FileUpload({ docKey, label, required, value, onChange }) {
    const handle = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = () => {
            const b64 = String(reader.result).split(",")[1];
            onChange({ b64, name: file.name, content_type: file.type || "application/pdf" });
        };
        reader.readAsDataURL(file);
    };
    return (
        <div>
            <label className={labelCls}>{label} {required && "*"}</label>
            <label data-testid={`apply-upload-${docKey}`}
                className={`flex items-center gap-2 px-3 py-2.5 border border-dashed rounded-lg text-sm cursor-pointer transition-colors ${value ? "border-success/50 bg-success/5 text-success" : "border-border text-muted-foreground hover:border-phoenix-navy/40"}`}>
                {value ? <CheckCircle2 className="w-4 h-4 shrink-0" /> : <Upload className="w-4 h-4 shrink-0" />}
                <span className="truncate">{value ? value.name : "Click to upload (PDF, JPG, PNG)"}</span>
                <input type="file" accept=".pdf,.jpg,.jpeg,.png" className="hidden" onChange={handle} />
            </label>
        </div>
    );
}

// Submit-time validation shared by the public application and invited onboarding.
export function validateCredentials({ form, licenses, docs, isProvider, supRequired, supTriggerStates, showField }) {
    if (showField("resume") && !docs.resume) return "Résumé / CV upload is required.";
    if (showField("state_license")) {
        for (let i = 0; i < licenses.length; i++) {
            if (!docs[`license_document_${i}`]) return `Upload the license document for license #${i + 1}.`;
        }
    }
    if (!docs.drivers_license_front || !docs.drivers_license_back) {
        return "Driver's license uploads (front and back) are required.";
    }
    if (showField("dea")) {
        if (!form.dea_number) return "DEA number is required.";
        if (!docs.dea_document) return "DEA registration document upload is required.";
    }
    if (isProvider) {
        if (showField("board_certification") && !docs.board_certification_document) {
            return "Board certification document is required for prescribers.";
        }
    }
    if (showField("malpractice_insurance") && !docs.malpractice_certificate) {
        return "Malpractice insurance certificate is required.";
    }
    if (supRequired) {
        if (!form.supervising_physician_first_name || !form.supervising_physician_last_name) {
            return `Your licensure state(s) (${supTriggerStates.join(", ")}) require a supervising/collaborating physician. Add their name.`;
        }
        if (!form.collaborative_agreement_expiration) {
            return "A Collaborative Practice Agreement expiration date is required (renews annually).";
        }
        if (!docs.collaborative_practice_agreement) {
            return "Upload the signed Collaborative Practice Agreement document.";
        }
    }
    return null;
}

// Normalizes the credential-specific portion of the submit payload (everything
// except position/personal-info/W-9, which differ between the two contexts).
export function buildCredentialPayload({ form, licenses, degrees, docs, isProvider }) {
    const supervising_physician = (isProvider && form.supervising_physician_first_name) ? {
        first_name: form.supervising_physician_first_name,
        last_name: form.supervising_physician_last_name,
        title: form.supervising_physician_title || "MD",
        phone: form.supervising_physician_phone || null,
        email: form.supervising_physician_email || null,
        npi: form.supervising_physician_npi || null,
        dea: form.supervising_physician_dea || null,
    } : null;
    const payload = {
        dea_number: form.dea_number, dea_state: form.dea_state, dea_expiration: form.dea_expiration,
        controlled_substance_number: form.controlled_substance_number,
        controlled_substance_state: form.controlled_substance_state,
        controlled_substance_expiration: form.controlled_substance_expiration,
        drivers_license_expiration: form.drivers_license_expiration,
        supervising_physician,
        collaborative_agreement_expiration: form.collaborative_agreement_expiration || null,
        board_certification_board: form.board_certification_board,
        board_certification_type: form.board_certification_type,
        board_certification_number: form.board_certification_number,
        board_certification_expiration: form.board_certification_expiration,
        malpractice_insurance_carrier: form.malpractice_insurance_carrier,
        malpractice_policy_number: form.malpractice_policy_number,
        malpractice_expiration: form.malpractice_expiration,
        years_of_experience: form.years_of_experience === "" ? null : Number(form.years_of_experience),
        certifications: form.certifications,
        skills_narrative: form.skills_narrative,
        forensic_cst_qualified: !!form.forensic_cst_qualified,
        has_pending_criminal_charges: !!form.has_pending_criminal_charges,
        criminal_charges_disclosure: form.criminal_charges_disclosure,
        has_felony_history: !!form.has_felony_history,
        felony_history_disclosure: form.felony_history_disclosure,
        has_license_suspension: !!form.has_license_suspension,
        license_restriction_disclosure: form.license_restriction_disclosure,
        has_malpractice_claims: !!form.has_malpractice_claims,
        malpractice_claims_disclosure: form.malpractice_claims_disclosure,
        licenses: licenses.filter((l) => l.license_number),
        education_history: degrees.filter((d) => d.institution).map((d) => ({
            ...d, field_of_study: d.field_of_study || null, graduation_date: d.graduation_date || null,
        })),
        documents: docs,
    };
    ["dea_number", "dea_state", "dea_expiration", "controlled_substance_number",
     "controlled_substance_state", "controlled_substance_expiration", "drivers_license_expiration",
     "board_certification_board", "board_certification_type", "board_certification_number",
     "board_certification_expiration", "malpractice_insurance_carrier", "malpractice_policy_number",
     "malpractice_expiration", "certifications", "skills_narrative",
     "criminal_charges_disclosure", "felony_history_disclosure",
     "license_restriction_disclosure", "malpractice_claims_disclosure",
    ].forEach((k) => { if (payload[k] === "") payload[k] = null; });
    return payload;
}

// The full credentialing section stack (DEA → Disclosures), shared by both forms.
export function CredentialSections({
    form, set, licenses, setLic, setLicenses, degrees, setDeg, setDegrees,
    docs, setDoc, isProvider, appTpl, fieldMode, showField, supRequired, supTriggerStates, showSupervising,
}) {
    return (
        <>
            {showField("dea") && (
                <Section title="Provider Identifiers — DEA Registration *">
                    <div className="grid sm:grid-cols-3 gap-4">
                        <div>
                            <label className={labelCls}>DEA state</label>
                            <input data-testid="apply-dea-state" className={inputCls} placeholder="State abbreviation" maxLength={2}
                                value={form.dea_state} onChange={(e) => set("dea_state", e.target.value)} />
                        </div>
                        <div>
                            <label className={labelCls}>DEA number *</label>
                            <input data-testid="apply-dea-number" className={inputCls} placeholder="DEA Registration #"
                                value={form.dea_number} onChange={(e) => set("dea_number", e.target.value)} />
                        </div>
                        <div>
                            <label className={labelCls}>Expiration date</label>
                            <input type="date" className={inputCls} value={form.dea_expiration} onChange={(e) => set("dea_expiration", e.target.value)} />
                        </div>
                    </div>
                    <div className="mt-3">
                        <FileUpload docKey="dea_document" label="DEA registration document" required value={docs.dea_document} onChange={setDoc("dea_document")} />
                    </div>
                </Section>
            )}

            {isProvider && (
                <Section title="State Controlled-Substance Registration (CSR)" when={showField("controlled_substance")}>
                    <p className="text-sm text-amber-900 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2.5 mb-3">
                        Some states require a separate state Controlled-Substance Registration in addition to your DEA. <b>You must upload it if your state requires one.</b>
                    </p>
                    <div className="grid sm:grid-cols-3 gap-4">
                        <div>
                            <label className={labelCls}>CSR state</label>
                            <input data-testid="apply-csr-state" className={inputCls} placeholder="State abbreviation" maxLength={2}
                                value={form.controlled_substance_state} onChange={(e) => set("controlled_substance_state", e.target.value)} />
                        </div>
                        <div>
                            <label className={labelCls}>CSR number</label>
                            <input data-testid="apply-csr-number" className={inputCls} placeholder="Registration #"
                                value={form.controlled_substance_number} onChange={(e) => set("controlled_substance_number", e.target.value)} />
                        </div>
                        <div>
                            <label className={labelCls}>Expiration date</label>
                            <input data-testid="apply-csr-exp" type="date" className={inputCls}
                                value={form.controlled_substance_expiration} onChange={(e) => set("controlled_substance_expiration", e.target.value)} />
                        </div>
                    </div>
                    <div className="mt-3 max-w-md">
                        <FileUpload docKey="controlled_substance_document" label="Controlled-substance registration upload" value={docs.controlled_substance_document} onChange={setDoc("controlled_substance_document")} />
                    </div>
                </Section>
            )}

            {isProvider && showSupervising && (
                <Section title="Supervising / Collaborating Physician">
                    <div data-testid="supervising-physician-section" className={`rounded-lg border p-4 ${supRequired ? "border-amber-300 bg-amber-50/60" : "border-border bg-secondary/20"}`}>
                        <p className="text-sm text-muted-foreground mb-3">
                            {supRequired
                                ? <>Your licensure state(s) <b className="text-amber-700">{supTriggerStates.join(", ")}</b> require a supervising/collaborating physician and a signed Collaborative Practice Agreement (CPA) for mid-level prescribers. These fields and the CPA upload are required.</>
                                : <>Optional. If you practice under a supervising/collaborating physician, add their details — they will print on your prescriptions (informational). Required automatically for mid-level prescribers in supervision states.</>}
                        </p>
                        <div className="grid sm:grid-cols-2 gap-4">
                            <div>
                                <label className={labelCls}>First name{supRequired ? " *" : ""}</label>
                                <input data-testid="sup-first-name" className={inputCls} value={form.supervising_physician_first_name}
                                    onChange={(e) => set("supervising_physician_first_name", e.target.value)} />
                            </div>
                            <div>
                                <label className={labelCls}>Last name{supRequired ? " *" : ""}</label>
                                <input data-testid="sup-last-name" className={inputCls} value={form.supervising_physician_last_name}
                                    onChange={(e) => set("supervising_physician_last_name", e.target.value)} />
                            </div>
                            <div>
                                <label className={labelCls}>Title</label>
                                <select data-testid="sup-title" className={inputCls} value={form.supervising_physician_title}
                                    onChange={(e) => set("supervising_physician_title", e.target.value)}>
                                    <option value="MD">MD</option>
                                    <option value="DO">DO</option>
                                </select>
                            </div>
                            <div>
                                <label className={labelCls}>Contact phone</label>
                                <input data-testid="sup-phone" className={inputCls} value={form.supervising_physician_phone}
                                    onChange={(e) => set("supervising_physician_phone", e.target.value)} />
                            </div>
                            <div>
                                <label className={labelCls}>Email</label>
                                <input data-testid="sup-email" type="email" className={inputCls} value={form.supervising_physician_email}
                                    onChange={(e) => set("supervising_physician_email", e.target.value)} />
                            </div>
                            <div>
                                <label className={labelCls}>Supervising physician NPI</label>
                                <input data-testid="sup-npi" className={inputCls} maxLength={10} value={form.supervising_physician_npi}
                                    onChange={(e) => set("supervising_physician_npi", e.target.value)} />
                            </div>
                            <div>
                                <label className={labelCls}>Supervising physician DEA</label>
                                <input data-testid="sup-dea" className={inputCls} value={form.supervising_physician_dea}
                                    onChange={(e) => set("supervising_physician_dea", e.target.value)} />
                            </div>
                            <div>
                                <label className={labelCls}>CPA expiration (renews annually){supRequired ? " *" : ""}</label>
                                <input data-testid="sup-cpa-expiration" type="date" className={inputCls} value={form.collaborative_agreement_expiration}
                                    onChange={(e) => set("collaborative_agreement_expiration", e.target.value)} />
                            </div>
                        </div>
                        <div className="mt-3">
                            <FileUpload docKey="collaborative_practice_agreement"
                                label={`Collaborative Practice Agreement${supRequired ? " *" : ""}`}
                                value={docs.collaborative_practice_agreement} onChange={setDoc("collaborative_practice_agreement")} />
                        </div>
                    </div>
                </Section>
            )}

            <Section title="Professional Experience & Résumé">
                {showField("years_of_experience") && (
                    <div className="max-w-[220px] mb-4">
                        <label className={labelCls}>Years of experience</label>
                        <input data-testid="apply-years-experience" type="number" min="0" className={inputCls}
                            value={form.years_of_experience} onChange={(e) => set("years_of_experience", e.target.value)} />
                    </div>
                )}
                {showField("resume") && (
                    <div className="max-w-md">
                        <FileUpload docKey="resume" label="Résumé / CV upload" required={fieldMode("resume") === "required"}
                            value={docs.resume} onChange={setDoc("resume")} />
                        <p className="text-xs text-muted-foreground mt-1.5">Upload your current résumé / CV (PDF). We use this in place of a manual employment history.</p>
                    </div>
                )}
            </Section>

            <Section title="Education & Credentials">
                {degrees.map((deg, i) => (
                    <div key={i} className="bg-secondary/20 border border-border/60 rounded-lg p-3 mb-2">
                        <div className="flex items-center justify-between mb-2">
                            <span className="text-xs font-semibold text-phoenix-navy uppercase tracking-wider">Degree {i + 1}</span>
                            {degrees.length > 1 && (
                                <button type="button" onClick={() => setDegrees((ds) => ds.filter((_, idx) => idx !== i))}
                                    className="p-1 text-muted-foreground hover:text-destructive"><Trash2 className="w-3.5 h-3.5" /></button>
                            )}
                        </div>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                            <input data-testid={`apply-degree-institution-${i}`} className={inputCls} placeholder="Institution / School"
                                value={deg.institution} onChange={(e) => setDeg(i, "institution", e.target.value)} />
                            <input data-testid={`apply-degree-field-${i}`} className={inputCls} placeholder="Field of study"
                                value={deg.field_of_study} onChange={(e) => setDeg(i, "field_of_study", e.target.value)} />
                            <select data-testid={`apply-degree-level-${i}`} className={inputCls} value={deg.degree_level}
                                onChange={(e) => setDeg(i, "degree_level", e.target.value)}>
                                {DEGREE_LEVELS.map((l) => <option key={l} value={l}>{l}</option>)}
                            </select>
                            <input data-testid={`apply-degree-grad-${i}`} type="date" className={inputCls} title="Graduation date"
                                value={deg.graduation_date} onChange={(e) => setDeg(i, "graduation_date", e.target.value)} />
                        </div>
                    </div>
                ))}
                <button type="button" data-testid="apply-add-degree" onClick={() => setDegrees((ds) => [...ds, { ...EMPTY_DEGREE, degree_level: isProvider ? "M.D." : "Master's" }])}
                    className="text-xs font-semibold text-phoenix-navy inline-flex items-center gap-1 hover:text-phoenix-orange mb-3">
                    <Plus className="w-3.5 h-3.5" /> Add degree
                </button>
                {!isProvider && (
                    <div>
                        <label className={labelCls}>Certifications (comma-separated)</label>
                        <input data-testid="apply-certifications" className={inputCls} placeholder="e.g. LCSW, LPC, CADC"
                            value={form.certifications} onChange={(e) => set("certifications", e.target.value)} />
                    </div>
                )}
            </Section>

            <Section title={isProvider ? "State License Information" : "License Information"}>
                {showField("state_license") && (<>
                {licenses.map((lic, i) => (
                    <div key={i} className="bg-secondary/20 border border-border/60 rounded-lg p-3 mb-2">
                        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 items-end">
                            <input data-testid={`apply-lic-type-${i}`} className={inputCls} placeholder={isProvider ? "Type (MD, NP, PA)" : "Type (LCSW, LPC, LMFT)"} value={lic.license_type} onChange={(e) => setLic(i, "license_type", e.target.value)} />
                            <input data-testid={`apply-lic-state-${i}`} className={inputCls} placeholder="License state" value={lic.state} onChange={(e) => setLic(i, "state", e.target.value)} />
                            <input data-testid={`apply-lic-number-${i}`} className={inputCls} placeholder="License number" value={lic.license_number} onChange={(e) => setLic(i, "license_number", e.target.value)} />
                            <input data-testid={`apply-lic-exp-${i}`} type="date" className={inputCls} value={lic.expiration_date} onChange={(e) => setLic(i, "expiration_date", e.target.value)} />
                            <button type="button" onClick={() => setLicenses((ls) => ls.length > 1 ? ls.filter((_, idx) => idx !== i) : ls)}
                                className="p-2 text-muted-foreground hover:text-destructive justify-self-start">
                                <Trash2 className="w-4 h-4" />
                            </button>
                        </div>
                        <div className="mt-2 max-w-md">
                            <FileUpload docKey={`license_document_${i}`} label={`License document upload — license #${i + 1}`} required
                                value={docs[`license_document_${i}`]} onChange={setDoc(`license_document_${i}`)} />
                        </div>
                    </div>
                ))}
                <button type="button" data-testid="apply-add-license" onClick={() => setLicenses((ls) => [...ls, { ...EMPTY_LICENSE }])}
                    className="text-xs font-semibold text-phoenix-navy inline-flex items-center gap-1 hover:text-phoenix-orange mb-4">
                    <Plus className="w-3.5 h-3.5" /> Add license
                </button>
                </>)}
                <div className="grid sm:grid-cols-3 gap-3 items-end">
                    <FileUpload docKey="drivers_license_front" label="Driver's license — front" required value={docs.drivers_license_front} onChange={setDoc("drivers_license_front")} />
                    <FileUpload docKey="drivers_license_back" label="Driver's license — back" required value={docs.drivers_license_back} onChange={setDoc("drivers_license_back")} />
                    <div>
                        <label className={labelCls}>Driver's license expiration *</label>
                        <input data-testid="apply-dl-exp" type="date" className={inputCls} value={form.drivers_license_expiration} onChange={(e) => set("drivers_license_expiration", e.target.value)} />
                    </div>
                </div>
            </Section>

            <Section title="Board Certification" when={showField("board_certification")}>
                {isProvider && (
                    <p className="text-sm text-amber-900 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2.5 mb-3">
                        <b>Required for prescribers.</b> Upload your current board certification and its expiration date.
                    </p>
                )}
                <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                        <label className={labelCls}>Certification type</label>
                        <input className={inputCls} placeholder={isProvider ? "e.g., ABPN, ABIM, ABFM" : "e.g., LCSW, LPC, LMFT"}
                            value={form.board_certification_type} onChange={(e) => set("board_certification_type", e.target.value)} />
                    </div>
                    <div>
                        <label className={labelCls}>Certifying board</label>
                        <input className={inputCls} value={form.board_certification_board} onChange={(e) => set("board_certification_board", e.target.value)} />
                    </div>
                    <div>
                        <label className={labelCls}>Certification number</label>
                        <input className={inputCls} value={form.board_certification_number} onChange={(e) => set("board_certification_number", e.target.value)} />
                    </div>
                    <div>
                        <label className={labelCls}>Expiration date{isProvider ? " *" : ""}</label>
                        <input type="date" className={inputCls} value={form.board_certification_expiration} onChange={(e) => set("board_certification_expiration", e.target.value)} />
                    </div>
                </div>
                <div className="mt-3 max-w-sm">
                    <FileUpload docKey="board_certification_document" label="Board certification upload" required={isProvider}
                        value={docs.board_certification_document} onChange={setDoc("board_certification_document")} />
                </div>
            </Section>

            <Section title="Malpractice Insurance" when={showField("malpractice_insurance")}>
                {appTpl?.insurance && (
                    <div data-testid="malpractice-requirement" className="mb-4 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-sm text-amber-900">
                        {appTpl.insurance.malpractice_required ? (
                            <><span className="font-semibold">Required:</span> Malpractice coverage of at least{" "}
                                <b>${Number(appTpl.insurance.malpractice_per_occurrence || 0).toLocaleString()}</b> per occurrence /{" "}
                                <b>${Number(appTpl.insurance.malpractice_aggregate || 0).toLocaleString()}</b> aggregate.</>
                        ) : <>Malpractice insurance details are optional for this role.</>}
                        {appTpl.insurance.general_liability_required && <> General liability insurance is also required.</>}
                    </div>
                )}
                <div data-testid="malpractice-info" className="mb-4 bg-phoenix-soft/40 border border-border rounded-lg px-4 py-3 text-sm text-phoenix-navy">
                    {isProvider ? (
                        <>
                            <span className="font-semibold">Required Coverage Limits for Psychiatric Nurse Practitioners:</span>{" "}
                            Minimum $1,000,000 per claim / $6,000,000 aggregate. Due to the higher risk exposure associated with
                            psychiatric prescribing and mental health treatment, Psychiatric NPs are required to carry higher
                            aggregate limits than general NPs. Policies must be occurrence-based or include tail coverage.
                        </>
                    ) : (
                        <>
                            <span className="font-semibold">Required Coverage Limits for Therapists (LCSW, LPC, LMFT, etc.):</span>{" "}
                            Minimum $1,000,000 per claim / $3,000,000 aggregate. This is the industry standard required by most
                            credentialing panels and employers. Higher limits (e.g. $2M/$4M) are accepted and encouraged.
                        </>
                    )}
                </div>
                <div className="grid sm:grid-cols-3 gap-4">
                    <div>
                        <label className={labelCls}>Insurance carrier</label>
                        <input className={inputCls} value={form.malpractice_insurance_carrier} onChange={(e) => set("malpractice_insurance_carrier", e.target.value)} />
                    </div>
                    <div>
                        <label className={labelCls}>Policy number</label>
                        <input className={inputCls} value={form.malpractice_policy_number} onChange={(e) => set("malpractice_policy_number", e.target.value)} />
                    </div>
                    <div>
                        <label className={labelCls}>Policy expiration date</label>
                        <input type="date" className={inputCls} value={form.malpractice_expiration} onChange={(e) => set("malpractice_expiration", e.target.value)} />
                    </div>
                </div>
                <div className="mt-3 max-w-sm">
                    <FileUpload docKey="malpractice_certificate" label="Malpractice insurance certificate" required
                        value={docs.malpractice_certificate} onChange={setDoc("malpractice_certificate")} />
                </div>
            </Section>

            <Section title="Skills & Expertise" when={showField("skills_narrative") || (isProvider && showField("forensic_cst"))}>
                {showField("skills_narrative") && (
                    <textarea data-testid="apply-skills" rows={3} className={inputCls}
                        placeholder={isProvider
                            ? "Describe your key clinical skills, areas of expertise, and experience with incarcerated populations…"
                            : "Describe your key skills and areas of expertise…"}
                        value={form.skills_narrative} onChange={(e) => set("skills_narrative", e.target.value)} />
                )}
                {isProvider && showField("forensic_cst") && (
                    <div className="mt-4 bg-secondary/20 border border-border/60 rounded-lg p-4">
                        <div className="flex items-start gap-2.5 text-sm text-muted-foreground mb-3">
                            <Scale className="w-4 h-4 mt-0.5 shrink-0 text-phoenix-navy" />
                            <div>
                                <span className="font-semibold text-phoenix-navy">Forensic Evaluations.</span>{" "}
                                Forensic competency evaluations (CST) require a court-designated psychiatrist or doctoral-level
                                psychologist. Enabling this allows you to complete court-ordered Psychiatric Competency Evaluation
                                forms within the platform.
                            </div>
                        </div>
                        <label className="flex items-center gap-2 text-sm text-foreground">
                            <input data-testid="apply-forensic-checkbox" type="checkbox" className="w-4 h-4 accent-phoenix-navy"
                                checked={form.forensic_cst_qualified} onChange={(e) => set("forensic_cst_qualified", e.target.checked)} />
                            I am qualified and court-authorized to perform forensic competency evaluations (CST)
                        </label>
                    </div>
                )}
            </Section>

            <Section title="Background Disclosures">
                {[["has_pending_criminal_charges", "criminal_charges_disclosure", "Do you have any pending criminal charges?"],
                  ["has_felony_history", "felony_history_disclosure", "Do you have a history of felony convictions?"],
                  ["has_license_suspension", "license_restriction_disclosure",
                      isProvider ? "Has your state license ever been suspended, revoked, or restricted?"
                                 : "Has your state license been suspended or have you been under investigation?"],
                  ["has_malpractice_claims", "malpractice_claims_disclosure", "Have you had any malpractice claims or settlements?"]].map(([flag, text, q]) => (
                    <div key={flag} className="mb-3">
                        <label className="flex items-center gap-2 text-sm text-foreground">
                            <input data-testid={`apply-${flag}`} type="checkbox" className="w-4 h-4 accent-phoenix-navy" checked={form[flag]}
                                onChange={(e) => set(flag, e.target.checked)} /> {q}
                        </label>
                        {form[flag] && (
                            <textarea rows={2} className={`${inputCls} mt-2`} placeholder="Please explain…"
                                value={form[text]} onChange={(e) => set(text, e.target.value)} />
                        )}
                    </div>
                ))}
            </Section>
        </>
    );
}
